import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';

const FAQPage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      question: 'Is my health data secure?',
      answer: 'Yes, we take data security seriously. All data is encrypted and stored securely. We never share your personal health information with third parties.'
    },
    {
      question: 'Can I share my data with my doctor?',
      answer: 'Absolutely! Premium subscribers can export comprehensive PDF reports of symptoms, labs, and timelines to share with healthcare providers.'
    },
    {
      question: 'What conditions does the app support?',
      answer: 'Our app is designed for PANDAS, PANS, and related conditions including Histamine Intolerance, Mast Cell Activation, MTHFR Gene Mutation, Mold Exposure, Co-infections, and more.'
    },
    {
      question: 'How does the AI health assistant work?',
      answer: 'Our AI is trained specifically on PANDAS/PANS health information. It can answer questions, provide educational content, and help you understand symptoms and treatments. It\'s not a replacement for medical advice.'
    },
    {
      question: 'Can I cancel my subscription anytime?',
      answer: 'Yes, you can cancel anytime. Your subscription will remain active until the end of your billing period.'
    },
    {
      question: 'Is there a mobile app?',
      answer: 'Calm Pathways is a Progressive Web App (PWA). You can install it on your phone directly from your browser for a native app-like experience without downloading from an app store.'
    },
    {
      question: 'What is the difference between Essential and Premium plans?',
      answer: 'The Essential plan ($9/month) includes core tracking features, knowledge base, AI chat, and community access. The Premium plan ($19/month) adds advanced features like medical timeline, lab tracking, PDF exports, smart correlations, and flare predictions.'
    },
    {
      question: 'How do I install the app on my phone?',
      answer: 'On iPhone: Open the app in Safari, tap the Share button, then "Add to Home Screen." On Android: Open in Chrome, tap the menu (three dots), then "Add to Home Screen" or "Install App."'
    },
    {
      question: 'Can multiple family members use one account?',
      answer: 'Yes! You can create multiple child profiles within a single account. This allows you to track symptoms and health data for multiple children separately.'
    },
    {
      question: 'How do I export my data?',
      answer: 'Premium subscribers can export their data as PDF reports. Go to the Medical Timeline page and click "Export PDF" to generate a comprehensive report you can share with healthcare providers.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Button 
          onClick={() => navigate(-1)} 
          variant="ghost" 
          className="mb-8 text-primary hover:text-primary/80"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>

        <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl p-8 mb-8">
          <div className="flex items-center gap-4 mb-4">
            <HelpCircle className="w-12 h-12 text-primary" strokeWidth={1.5} />
            <div>
              <h1 className="text-3xl md:text-4xl font-nunito font-bold text-[#2D2D2D]">
                Frequently Asked Questions
              </h1>
              <p className="text-[#57534E]">Got questions? We've got answers.</p>
            </div>
          </div>
          <p className="text-[#57534E] leading-relaxed">
            Find answers to common questions about Calm Pathways, our features, subscriptions, and how to get the most out of your experience.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <Card 
              key={i} 
              className="bg-white rounded-xl border border-stone-100 shadow-sm p-6 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
              data-testid={`faq-item-${i}`}
            >
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-[#2D2D2D] pr-4">{faq.question}</h3>
                {openFaq === i ? (
                  <ChevronUp className="w-5 h-5 text-primary flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-[#57534E] flex-shrink-0" />
                )}
              </div>
              {openFaq === i && (
                <p className="mt-4 text-[#57534E] leading-relaxed">{faq.answer}</p>
              )}
            </Card>
          ))}
        </div>

        <Card className="bg-white rounded-xl border border-stone-100 shadow-sm p-6 mt-8">
          <h2 className="text-xl font-nunito font-bold text-[#2D2D2D] mb-4">
            Still have questions?
          </h2>
          <p className="text-[#57534E] mb-4">
            Can't find the answer you're looking for? We're here to help.
          </p>
          <a 
            href="mailto:contact@meandmypandas.com" 
            className="inline-flex items-center text-primary hover:text-primary/80 font-medium"
          >
            Contact us at contact@meandmypandas.com
          </a>
        </Card>
      </div>
    </div>
  );
};

export default FAQPage;
